import { useCallback, useRef } from 'react';
import { numberPredicate } from 'js/utils/predicate.util';

import { getResponseData, parse, post } from '../helpers';
import { HOUSE_REGEX, POSTCODE_PROVIDER_ERROR } from '../constants';
import { useAddressLookupSwissConfig } from './useAddressLookupSwissConfig';

import * as Types from '../types';

export const useSearchHouseNumber = () => {
    const abortController = useRef<AbortController | null>(null);
    const prevSearch = useRef<string>('');
    const config = useAddressLookupSwissConfig();

    const prepareHouseNumberRequest = useCallback((
        searchText: string,
        retrievedAddress?: Types.IParsedAutoCompleteItem | null
    ): Types.IAddressLookupRequest => {
        return {
            request: {
                HouseKey: 0,
                HouseNumberAddition: '',
                Onrp: 0,
                StrId: 0,
                ZipAddition: '',
                Zipcode: '',
                HouseNumber: searchText,
                TownName: retrievedAddress?.city || '',
                Streetname: retrievedAddress?.streetName || ''
            },
            zipOrderMode: 0
        };
    }, []);

    return useCallback(async (
        searchText: string,
        retrievedAddress?: Types.IParsedAutoCompleteItem | null
    ): Promise<Types.IParsedAutoCompleteItem[]> => {
        prevSearch.current = searchText;
        abortController.current?.abort();
        abortController.current = new AbortController();

        if (searchText.length < 1 || !HOUSE_REGEX.test(searchText)) {
            throw {
                errorCode: POSTCODE_PROVIDER_ERROR.NOT_VALID_ERROR,
                errorMessage: 'playtech.mobile.system.form.addressLookupSwiss.internal.houseNumberNotValid'
            };
        }

        try {
            const params = prepareHouseNumberRequest(searchText, retrievedAddress);
            const response = await post(config.url, params, abortController.current?.signal);
            const data = await getResponseData(response);

            data.sort((row1, row2) => {
                const num1 = parseInt(row1.HouseNumber);
                const num2 = parseInt(row2.HouseNumber);

                return -numberPredicate(num1, num2);
            });

            return parse(data);
        } catch (error: any) {
            if (error?.errorCode === POSTCODE_PROVIDER_ERROR.ABORT_ERROR) {
                return [];
            }

            throw error;
        }
    }, [config, prepareHouseNumberRequest]);
};
